<template>
  <!-- Siempre usar el fallback CSS elegante que simula efectos glass -->
  <div
    :class="fallbackClasses"
    :style="fallbackStyles"
    @click="handleClick"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <slot />
  </div>
</template>

<script setup>
import { computed, ref, onMounted, inject } from 'vue';
import { useGlassEffect } from 'dashboard/composables/useGlassEffect';

const props = defineProps({
  /**
   * Preset predefinido de Chatventas
   * Opciones: 'chatventasCard', 'chatventasButton', 'chatventasModal',
   *          'chatventasNav', 'chatventasSubtle', 'chatventasPremium', 'chatventasBubble'
   */
  preset: {
    type: String,
    default: 'chatventasCard',
    validator: (value) => [
      'chatventasCard',
      'chatventasButton',
      'chatventasModal',
      'chatventasNav',
      'chatventasSubtle',
      'chatventasPremium',
      'chatventasBubble'
    ].includes(value)
  },

  /**
   * Configuraciones personalizadas que sobrescriben el preset
   */
  customConfig: {
    type: Object,
    default: () => ({})
  },

  /**
   * Clases CSS adicionales para el contenedor
   */
  class: {
    type: [String, Array, Object],
    default: ''
  },

  /**
   * Si debe usar lazy loading para optimizar rendimiento
   */
  lazy: {
    type: Boolean,
    default: false
  },

  /**
   * Threshold para lazy loading (distancia en pixels del viewport)
   */
  lazyThreshold: {
    type: Number,
    default: 100
  },

  /**
   * Forzar el renderizado incluso si los efectos están globalmente deshabilitados
   */
  forceRender: {
    type: Boolean,
    default: false
  },

  /**
   * Clases CSS para el fallback cuando glass está deshabilitado
   */
  fallbackClass: {
    type: [String, Array, Object],
    default: 'chatventas-glass-css-fallback'
  },

  /**
   * Contenedor de mouse personalizado (para efectos que responden al mouse)
   */
  mouseContainer: {
    type: Object,
    default: null
  }
});

// Inyectar configuración de debugging si está disponible
const debugConfig = inject('glassDebugConfig', null);

const emit = defineEmits(['click', 'mouseenter', 'mouseleave', 'glass-loaded', 'glass-error']);

// Composable para gestión de efectos glass
const { isGlassEnabled, getPreset, createCustomConfig, deviceCapabilities } = useGlassEffect();

// Estado para lazy loading
const isInViewport = ref(!props.lazy);
const glassContainer = ref(null);

// Configuración computada basada en preset, customConfig y debugConfig
const glassConfig = computed(() => {
  const baseConfig = getPreset(props.preset);
  if (!baseConfig) return null;

  // Aplicar configuración de debug si está disponible
  const finalConfig = {
    ...baseConfig,
    ...props.customConfig,
    ...(debugConfig?.value || {})
  };


  return createCustomConfig(finalConfig);
});// Clases para el componente glass CSS
const fallbackClasses = computed(() => {
  const classes = ['chatventas-glass-fallback', 'chatventas-glass-css-fallback'];

  if (props.fallbackClass) {
    if (typeof props.fallbackClass === 'string') {
      classes.push(props.fallbackClass);
    } else if (Array.isArray(props.fallbackClass)) {
      classes.push(...props.fallbackClass);
    } else {
      Object.keys(props.fallbackClass).forEach(key => {
        if (props.fallbackClass[key]) classes.push(key);
      });
    }
  }

  // Agregar clase específica del preset para el fallback CSS
  classes.push(`preset-${props.preset.replace('chatventas', '').toLowerCase()}`);

  if (props.class) {
    if (typeof props.class === 'string') {
      classes.push(props.class);
    } else if (Array.isArray(props.class)) {
      classes.push(...props.class);
    } else {
      Object.keys(props.class).forEach(key => {
        if (props.class[key]) classes.push(key);
      });
    }
  }

  return classes;
});

// Estilos para fallback con configuración dinámica
const fallbackStyles = computed(() => {
  if (!glassConfig.value) return {};

  const config = glassConfig.value;

  // Color mapping para tintes
  const tintColors = {
    blue: 'rgba(59, 130, 246, ',
    green: 'rgba(16, 185, 129, ',
    purple: 'rgba(139, 92, 246, ',
    pink: 'rgba(236, 72, 153, ',
    orange: 'rgba(249, 115, 22, ',
    red: 'rgba(239, 68, 68, ',
    cyan: 'rgba(6, 182, 212, ',
    yellow: 'rgba(234, 179, 8, ',
    gray: 'rgba(107, 114, 128, ',
    white: 'rgba(255, 255, 255, '
  };

  const baseTintColor = tintColors[config.tintColor] || tintColors.blue;
  const tintIntensity = typeof config.tintIntensity === 'number' ? config.tintIntensity : 0.1;
  const glowIntensity = typeof config.glowIntensity === 'number' ? config.glowIntensity : 0.5;
  const bgOpacity = typeof config.backgroundOpacity === 'number' ? config.backgroundOpacity : 0.05;
  const gradDir = typeof config.gradientDirection === 'number' ? config.gradientDirection : 135;

  // Soportar padding como número (px) o string CSS
  const paddingValue = (() => {
    const p = config.padding;
    if (typeof p === 'number') return `${p}px`;
    if (typeof p === 'string') return p;
    return '12px';
  })();

  // Soportar borderWidth como número o string
  const borderWidthValue = (() => {
    const bw = config.borderWidth;
    if (typeof bw === 'number') return `${bw}px`;
    if (typeof bw === 'string') return bw;
    return '1px';
  })();

  // Capa de tinte principal: doble parada con el mismo color para reforzar el tono
  const tintStop1 = `${baseTintColor}${Math.min(tintIntensity * 0.85, 1)})`;
  const tintStop2 = `${baseTintColor}${tintIntensity})`;
  const layerTint = `linear-gradient(${gradDir}deg, ${tintStop1}, ${tintStop2})`;
  // Velo suave: aporta lechoso/opaco sin deslavar el color
  const layerSoft = `linear-gradient(180deg, rgba(255, 255, 255, ${Math.max(bgOpacity, 0.01)}), rgba(255, 255, 255, ${Math.max(bgOpacity * 0.6, 0.01)}))`;

  // Chatventas: render PLANO (sin efecto glass) — tinte sólido suave por variante,
  // sin blur, sin velo translúcido, sin sombra/glow. Mantiene forma y color por variante.
  void layerTint; void layerSoft; void glowIntensity;
  const styles = {
    padding: paddingValue,
    borderRadius: `${config.cornerRadius || 12}px`,
    background: `${baseTintColor}${Math.max(tintIntensity, 0.12)})`,
    border: `${borderWidthValue} solid ${baseTintColor}0.22)`,
  };

  // Añadir animaciones si están habilitadas
  if (config.animationEnabled) {
    styles.transition = `all ${config.animationSpeed || 1}s cubic-bezier(0.4, 0, 0.2, 1)`;
  }

  return styles;
});// Determinar si debe renderizar el efecto glass (siempre true para CSS fallback)
const shouldRenderGlass = computed(() => {
  return isGlassEnabled.value || props.forceRender;
});

// Intersection Observer para lazy loading
const setupLazyLoading = () => {
  if (!props.lazy || !glassContainer.value) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          isInViewport.value = true;
          observer.disconnect();
          emit('glass-loaded');
        }
      });
    },
    {
      rootMargin: `${props.lazyThreshold}px`,
      threshold: 0.1
    }
  );

  observer.observe(glassContainer.value);
};

// Event handlers
const handleClick = (event) => {
  emit('click', event);
};

const handleMouseEnter = (event) => {
  emit('mouseenter', event);
};

const handleMouseLeave = (event) => {
  emit('mouseleave', event);
};

// Lifecycle
onMounted(() => {
  // Emitir evento de que el glass está listo (usando CSS fallback)
  emit('glass-loaded');

  if (props.lazy) {
    setupLazyLoading();
  }
});

// Exposed methods for parent components
defineExpose({
  /**
   * Forzar la carga del efecto glass (útil para lazy loading manual)
   */
  forceLoad: () => {
    isInViewport.value = true;
  },

  /**
   * Obtener la configuración actual del glass
   */
  getConfig: () => glassConfig.value,

  /**
   * Verificar si el glass está activo
   */
  isActive: () => shouldRenderGlass.value,
});
</script>

<style scoped>
.chatventas-glass {
  /* Clases base para el componente glass */
  transition: all 0.2s ease-in-out;
}

.chatventas-glass-fallback {
  /* Clases base para el fallback */
  transition: all 0.2s ease-in-out;
}

/* Fallback CSS avanzado que simula efectos glass */
.chatventas-glass-css-fallback {
  /* Base glass effect usando CSS puro */
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1),
    rgba(255, 255, 255, 0.05)
  );
  backdrop-filter: blur(10px) saturate(180%);
  -webkit-backdrop-filter: blur(10px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  box-shadow:
    0 8px 32px 0 rgba(31, 38, 135, 0.37),
    inset 0 1px 0 0 rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;

  /* Efectos de hover */
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.chatventas-glass-css-fallback:hover {
  transform: translateY(-2px);
  box-shadow:
    0 12px 40px 0 rgba(31, 38, 135, 0.45),
    inset 0 1px 0 0 rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.3);
}

/* Efecto de luz interna */
.chatventas-glass-css-fallback::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.4),
    transparent
  );
}

/* Variantes por preset */
.chatventas-glass-css-fallback.preset-card {
  padding: 20px 24px;
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1),
    rgba(255, 255, 255, 0.05)
  );
  backdrop-filter: blur(10px) saturate(125%);
  -webkit-backdrop-filter: blur(10px) saturate(125%);
  border-color: rgba(255, 255, 255, 0.2);
}

.chatventas-glass-css-fallback.preset-button {
  padding: 12px 16px;
  border-radius: 8px;
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.1),
    rgba(59, 130, 246, 0.05)
  );
  border-color: rgba(59, 130, 246, 0.3);
}

.chatventas-glass-css-fallback.preset-modal {
  padding: 24px 32px;
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.15),
    rgba(255, 255, 255, 0.08)
  );
  backdrop-filter: blur(20px) saturate(190%);
  -webkit-backdrop-filter: blur(20px) saturate(190%);
}

.chatventas-glass-css-fallback.preset-nav {
  padding: 16px 20px;
  border-radius: 8px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.08),
    rgba(255, 255, 255, 0.03)
  );
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.chatventas-glass-css-fallback.preset-bubble {
  padding: 8px 12px;
  border-radius: 100px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.08),
    rgba(255, 255, 255, 0.04)
  );
  backdrop-filter: blur(6px) saturate(120%);
  -webkit-backdrop-filter: blur(6px) saturate(120%);
  border-color: rgba(255, 255, 255, 0.2);
  box-shadow:
    0 4px 16px 0 rgba(31, 38, 135, 0.15),
    inset 0 1px 0 0 rgba(255, 255, 255, 0.2);
}

.chatventas-glass-css-fallback.preset-premium {
  padding: 28px 36px;
  border-radius: 20px;
  background: linear-gradient(
    135deg,
    rgba(168, 85, 247, 0.15),
    rgba(168, 85, 247, 0.08)
  );
  border-color: rgba(168, 85, 247, 0.4);
  box-shadow:
    0 12px 48px 0 rgba(168, 85, 247, 0.3),
    inset 0 1px 0 0 rgba(255, 255, 255, 0.3);
}

/* Modo oscuro */
@media (prefers-color-scheme: dark) {
  .chatventas-glass-css-fallback {
    background: linear-gradient(
      135deg,
      rgba(15, 23, 42, 0.8),
      rgba(15, 23, 42, 0.6)
    );
    border-color: rgba(148, 163, 184, 0.2);
    box-shadow:
      0 8px 32px 0 rgba(0, 0, 0, 0.5),
      inset 0 1px 0 0 rgba(148, 163, 184, 0.1);
  }

  .chatventas-glass-css-fallback::before {
    background: linear-gradient(
      90deg,
      transparent,
      rgba(148, 163, 184, 0.2),
      transparent
    );
  }
}

/* Animación de entrada suave */
.chatventas-glass,
.chatventas-glass-fallback,
.chatventas-glass-css-fallback {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Optimizaciones de rendimiento */
.chatventas-glass,
.chatventas-glass-css-fallback {
  will-change: transform;
  backface-visibility: hidden;
}

/* Estilos responsivos */
@media (max-width: 768px) {
  .chatventas-glass,
  .chatventas-glass-css-fallback {
    /* Reducir efectos en móviles para mejor rendimiento */
    transform: translateZ(0);
  }

  .chatventas-glass-css-fallback {
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
  }
}

/* Modo de alto contraste */
@media (prefers-contrast: high) {
  .chatventas-glass-fallback,
  .chatventas-glass-css-fallback {
    border-width: 2px;
    border-color: currentColor;
    background: rgba(255, 255, 255, 0.9);
  }
}

/* Preferencias de movimiento reducido */
@media (prefers-reduced-motion: reduce) {
  .chatventas-glass,
  .chatventas-glass-fallback,
  .chatventas-glass-css-fallback {
    animation: none;
    transition: none;
  }

  .chatventas-glass-css-fallback::after {
    display: none;
  }
}

/* Soporte para navegadores sin backdrop-filter */
@supports not (backdrop-filter: blur(10px)) {
  .chatventas-glass-css-fallback {
    background: rgba(255, 255, 255, 0.85);
    border: 1px solid rgba(0, 0, 0, 0.1);
  }

  @media (prefers-color-scheme: dark) {
    .chatventas-glass-css-fallback {
      background: rgba(15, 23, 42, 0.9);
      border-color: rgba(148, 163, 184, 0.3);
    }
  }
}

/* Chatventas: desactivar el efecto glass — render plano (sin blur/translucidez/sombra/glow). */
.chatventas-glass-css-fallback,
.chatventas-glass-css-fallback.preset-card,
.chatventas-glass-css-fallback.preset-button,
.chatventas-glass-css-fallback.preset-modal,
.chatventas-glass-css-fallback.preset-nav,
.chatventas-glass-css-fallback.preset-subtle,
.chatventas-glass-css-fallback.preset-premium,
.chatventas-glass-css-fallback.preset-bubble {
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  box-shadow: none !important;
  transition: none !important;
}
.chatventas-glass-css-fallback::before,
.chatventas-glass-css-fallback::after {
  content: none !important;
  display: none !important;
}
.chatventas-glass-css-fallback:hover {
  transform: none !important;
  box-shadow: none !important;
}
</style>
